import { FunctionComponent, useState, useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";

import { SavedFilterSelector } from "@/pages/Private/components/SavedFilterSelector/SavedFilterSelector";

import { IFilter } from "../../List/constants/general-filter";
import { IFilterWithValues } from "../../List/components/FilterModal";
import { useFilters } from "../../List/utils/useFilters";

const SEMANTIC_PROSPECTS_KEY = "__semanticSearchProspects";
const SEMANTIC_ORGANIZATIONS_KEY = "__semanticSearchOrganizations";

export interface ContactsFilterModalProps {
	showFilterModal: boolean;
	setShowFilterModal: (value: boolean) => void;
	filter?: IFilterWithValues;
	setFilter: (value: IFilterWithValues | undefined) => void;
	applyFilters?: (opts: {
		filter?: IFilterWithValues | undefined;
		semanticSearchProspects?: string;
		semanticSearchOrganizations?: string;
	}) => void;
	filterDataType: IFilter[];
	semanticSearchProspects?: string;
	semanticSearchOrganizations?: string;
	setSemanticSearchProspects?: (value: string) => void;
	setSemanticSearchOrganizations?: (value: string) => void;
}

export const ContactsFilterModal: FunctionComponent<ContactsFilterModalProps> = ({
	showFilterModal,
	setShowFilterModal,
	filter,
	setFilter,
	applyFilters,
	filterDataType,
	semanticSearchProspects = "",
	semanticSearchOrganizations = "",
	setSemanticSearchProspects,
	setSemanticSearchOrganizations,
}) => {
	const { t } = useTranslation();

	const { renderFilters, filterValue, setFilterValue, setFilterType } = useFilters({
		filter,
		filterDefinitions: filterDataType,
	});

	const [localSemanticProspects, setLocalSemanticProspects] = useState(semanticSearchProspects);
	const [localSemanticOrganizations, setLocalSemanticOrganizations] = useState(
		semanticSearchOrganizations
	);

	useEffect(() => {
		setLocalSemanticProspects(semanticSearchProspects);
	}, [semanticSearchProspects]);

	useEffect(() => {
		setLocalSemanticOrganizations(semanticSearchOrganizations);
	}, [semanticSearchOrganizations]);

	const onSubmit = () => {
		if (applyFilters) {
			// Use combined setter to avoid race conditions with multiple setSearchParams calls
			applyFilters({
				filter: filterValue,
				semanticSearchProspects: localSemanticProspects,
				semanticSearchOrganizations: localSemanticOrganizations,
			});
		} else {
			filterValue && setFilter(filterValue);
			setSemanticSearchProspects?.(localSemanticProspects);
			setSemanticSearchOrganizations?.(localSemanticOrganizations);
		}
	};

	const resetFilter = () => {
		setFilterType({});
		setFilterValue({});
		setLocalSemanticProspects("");
		setLocalSemanticOrganizations("");
	};

	const handleLoadFilter = (loadedFilter: IFilterWithValues) => {
		const availableFilterParams = new Set(filterDataType.map((f) => f.filterParam));
		const compatibleFilter: IFilterWithValues = {};

		for (const [key, value] of Object.entries(loadedFilter)) {
			if (key === SEMANTIC_PROSPECTS_KEY) {
				setLocalSemanticProspects((value.value as string) || "");
				continue;
			}

			if (key === SEMANTIC_ORGANIZATIONS_KEY) {
				setLocalSemanticOrganizations((value.value as string) || "");
				continue;
			}

			if (availableFilterParams.has(value.filterParam)) {
				compatibleFilter[key] = value;
			}
		}

		setFilterValue(compatibleFilter);
		setFilterType(
			Object.fromEntries(Object.entries(compatibleFilter).map(([key, val]) => [key, val.type]))
		);
	};

	const combinedFilterForSave = useMemo(() => {
		const combined: IFilterWithValues = { ...(filterValue || {}) };

		if (localSemanticProspects) {
			combined[SEMANTIC_PROSPECTS_KEY] = {
				value: localSemanticProspects,
				filterParam: SEMANTIC_PROSPECTS_KEY,
				type: "textContains",
			};
		}

		if (localSemanticOrganizations) {
			combined[SEMANTIC_ORGANIZATIONS_KEY] = {
				value: localSemanticOrganizations,
				filterParam: SEMANTIC_ORGANIZATIONS_KEY,
				type: "textContains",
			};
		}

		return combined;
	}, [filterValue, localSemanticProspects, localSemanticOrganizations]);

	return (
		<Modal
			handleClear={() => resetFilter()}
			handleClose={() => setShowFilterModal(false)}
			handleSave={() => {
				onSubmit();
				setShowFilterModal(false);
			}}
			isOpened={showFilterModal}
			size="lg"
			submitButtonText={t("basics.confirm")}
			title={t("basics.filter")}
		>
			<SavedFilterSelector currentFilter={combinedFilterForSave} onLoadFilter={handleLoadFilter} />
			<div className="flex flex-col gap-4 mb-4">
				{setSemanticSearchProspects && (
					<InputField
						handleChange={(e) => setLocalSemanticProspects(e.target.value)}
						label={t("contacts.semanticSearchProspectsLabel")}
						name="semanticSearchProspects"
						placeholder={t("contacts.semanticSearchProspectsPlaceholder")}
						value={localSemanticProspects}
					/>
				)}
				{setSemanticSearchOrganizations && (
					<InputField
						handleChange={(e) => setLocalSemanticOrganizations(e.target.value)}
						label={t("contacts.semanticSearchOrganizationsLabel")}
						name="semanticSearchOrganizations"
						placeholder={t("contacts.semanticSearchOrganizationsPlaceholder")}
						value={localSemanticOrganizations}
					/>
				)}
			</div>
			<div className="flex flex-col">
				{renderFilters(filterDataType, undefined, undefined, true)}
			</div>
		</Modal>
	);
};
